import { useCallback, useEffect, useRef, useState } from 'react';

import { config } from '@/config/env';
import { logger } from '@/logger';

import { buildFireflyEditorConfig } from './fireflyHostAdapter';
import { hideFireflyCostIndicator } from './hideCostIndicator';
import { hideDisabledSizePicker } from './hideDisabledSizePicker';
import { resolveImageInputUrl } from './imageMarshalling';
import { loadFireflyEditor } from './loadFireflyEditor';
import type {
	FireflyEditorLoader,
	FireflyImageEditorHandle,
	FireflyTool,
} from './types';

export type FireflyEditorStatus = 'idle' | 'loading' | 'ready' | 'error';

interface UseFireflyEditorArgs {
	/** Whether the editor should be mounted/active (modal open). */
	open: boolean;
	/** Source image URL to open once the editor is ready. */
	imageUrl: string | null;
	/** Tool set; defaults to the four proven-free tools. */
	enabledTools?: readonly FireflyTool[];
	/**
	 * Loader injection seam. Defaults to the production bundle loader; tests
	 * pass a fake to validate the whole lifecycle without the real bundle.
	 */
	loader?: FireflyEditorLoader;
	/** Live token getter; defaults inside the host adapter to localStorage. */
	getAccessToken?: () => string | null;
	/**
	 * Current user id. When set, PROMPT generation routes through the Bridgeway
	 * backend Firefly route (service token); omit to keep prompt on Colligo.
	 */
	userId?: string | null;
}

interface UseFireflyEditorResult {
	/** Attach to the editor container element. */
	containerRef: React.RefObject<HTMLDivElement | null>;
	status: FireflyEditorStatus;
	/** Read the edited result as a Blob (null if no edit was made). */
	getEditedImageBlob: () => Promise<Blob | null>;
}

/**
 * Drives one Firefly editor instance for the lifetime of an open modal
 * (ADO-1412). On `open`, it lazy-loads the bundle, mounts the editor into
 * `containerRef`, opens `imageUrl`, and tracks ready/error status. On close or
 * unmount, it destroys the instance and releases resources. Image-out is read
 * on demand via `getEditedImageBlob`.
 */
export function useFireflyEditor({
	open,
	imageUrl,
	enabledTools,
	loader = loadFireflyEditor,
	getAccessToken,
	userId,
}: UseFireflyEditorArgs): UseFireflyEditorResult {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const handleRef = useRef<FireflyImageEditorHandle | null>(null);
	const [status, setStatus] = useState<FireflyEditorStatus>('idle');

	// Hold the mutable callbacks in refs so an inline `getAccessToken`/`loader`
	// (new identity every render) does NOT thrash the mount effect. The effect
	// keys only on values whose change should actually re-open the editor.
	const loaderRef = useRef(loader);
	loaderRef.current = loader;
	const getAccessTokenRef = useRef(getAccessToken);
	getAccessTokenRef.current = getAccessToken;
	const userIdRef = useRef(userId);
	userIdRef.current = userId;

	useEffect(() => {
		if (!open) return;
		const container = containerRef.current;
		if (!container || !imageUrl) return;

		let cancelled = false;
		let readyUnsub: (() => void) | undefined;
		// Suppresses Adobe's "Standard feature" credit/cost indicator inside the
		// editor's shadow DOM (the bundle exposes no config/CSS lever for it).
		const costHider = hideFireflyCostIndicator(container);
		// Suppresses the size/aspect-ratio picker, which is permanently disabled
		// in edit mode (output is locked to the source image size).
		const sizePickerHider = hideDisabledSizePicker(container);
		setStatus('loading');

		(async () => {
			try {
				const editorConfig = buildFireflyEditorConfig({
					container,
					enabledTools,
					getAccessToken: getAccessTokenRef.current,
					beaconEnv: config.ENV,
					userId: userIdRef.current,
				});
				const handle = await loaderRef.current(editorConfig);
				if (cancelled) {
					handle.destroy();
					return;
				}
				handleRef.current = handle;

				readyUnsub = handle.onReady(() => {
					if (!cancelled) {
						costHider.refresh();
						sizePickerHider.refresh();
						setStatus('ready');
					}
				});

				// Open by URL — the editor fetches it and builds its own BlobRef.
				await handle.openImageFromUrl(resolveImageInputUrl(imageUrl));
			} catch (err) {
				if (!cancelled) {
					logger.error('Firefly editor failed to initialize', err);
					setStatus('error');
				}
			}
		})();

		return () => {
			cancelled = true;
			readyUnsub?.();
			costHider.disconnect();
			sizePickerHider.disconnect();
			handleRef.current?.destroy();
			handleRef.current = null;
			setStatus('idle');
		};
	}, [open, imageUrl, enabledTools]);

	const getEditedImageBlob = useCallback(async (): Promise<Blob | null> => {
		const handle = handleRef.current;
		if (!handle) return null;
		return handle.getEditedImageBlob();
	}, []);

	return { containerRef, status, getEditedImageBlob };
}
